import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  REPORTS_BASE_URL,
  PAYMENT_REPORT_FACT_API,
} from "../../../../Constants/Config";

const initialState = {
  loading: false,
  paymentData: {},
  totals: {
    collected: 0,
    refunds: 0,
    collected_transaction: 0,
    refund_transaction: 0,
    transactions: 0,
    net_tendered: 0,
  },
  byLocation: [],
  status: false,
  error: "",
};

let cancelTokenSource;

// Fact-table Payment Report. Same `result` shape as PHP
// Sale_summary_api/payment_report, so the table/chart mapping is unchanged -
// but it honours outlet_ids, so the Outlet picker actually filters.
export const fetchPaymentReportFactData = createAsyncThunk(
  "PaymentReportFact/fetchPaymentReportFactData",
  async (data, { rejectWithValue }) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    cancelTokenSource = axios.CancelToken.source();

    try {
      const { token, ...dataNew } = data;

      // The fact endpoint is FastAPI and reads Form() fields, so the body is a
      // real multipart FormData and the Content-Type header is left to axios
      // so it can generate the boundary.
      const formData = new FormData();
      const append = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      };

      // merchant_id must stay the LOGGED-IN merchant: auth validates the token
      // against it, so sending a selected outlet here is a 401. outlet_ids is
      // what actually scopes the report.
      append("merchant_id", dataNew?.merchant_id);
      // DashDateRangeComponent's Outlet picker is multi-select and emits
      // `stores` as a comma-joined merchant_id list; that IS the multi-location
      // filter. Fall back to the logged-in merchant so a single-store login
      // stays scoped rather than widening to everything.
      append(
        "outlet_ids",
        dataNew?.outlet_ids || dataNew?.stores || dataNew?.merchant_id
      );
      append("start_date", dataNew?.start_date);
      append("end_date", dataNew?.end_date);
      // '2' = Online, '3' = In-Store, omitted = both.
      append("order_env", dataNew?.order_env);
      append("token_id", dataNew?.token_id);
      append("login_type", dataNew?.login_type);
      append("session_id", dataNew?.session_id);

      const res = await axios.post(
        REPORTS_BASE_URL + PAYMENT_REPORT_FACT_API,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSource.token,
        }
      );

      if (res.data.status === true) {
        return {
          paymentData: res.data.result || {},
          totals: res.data.totals || initialState.totals,
          byLocation: res.data.by_location || [],
          status: true,
        };
      }
      return {
        paymentData: {},
        totals: initialState.totals,
        byLocation: [],
        status: false,
      };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const PaymentReportFactSlice = createSlice({
  name: "PaymentReportFact",
  initialState,
  reducers: {
    updateStateOfPaymentFact: (state) => {
      state.paymentData = {};
      state.totals = initialState.totals;
      state.byLocation = [];
      state.status = false;
    },
    // The table sorts in place, so it writes the reordered object back.
    setPaymentFactData: (state, action) => {
      state.paymentData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaymentReportFactData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPaymentReportFactData.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentData = action.payload.paymentData;
      state.totals = action.payload.totals;
      state.byLocation = action.payload.byLocation;
      state.status = action.payload.status;
      state.error = "";
    });
    builder.addCase(fetchPaymentReportFactData.rejected, (state, action) => {
      state.loading = false;
      state.paymentData = {};
      state.totals = initialState.totals;
      state.byLocation = [];
      state.status = false;
      state.error = action.error.message;
    });
  },
});

export const { updateStateOfPaymentFact, setPaymentFactData } =
  PaymentReportFactSlice.actions;
export default PaymentReportFactSlice.reducer;
